// Narrator — Phase 2 polish, AI-narrated tour.
//
// When toggled on via the 🎙 button, the browser TTS (window.speechSynthesis)
// speaks the active room's `narration` field on every room change so the
// tour walks itself through the apartment. Operator can leave the room
// silently if `narration` is undefined.
//
// pt-BR voice selection: tries to find a Brazilian Portuguese voice
// (lang starts with 'pt') and falls back to the default if none is
// installed. Quality varies by OS — macOS "Luciana" is great; Windows
// "Maria" is OK; Android Google TTS pt-BR is solid; Chrome ships with
// Google's online voices that work great when connected.
//
// Upgrade path → ElevenLabs / Azure Neural TTS: keep this public API
// (`isEnabled`, `setEnabled`, `speak`, `cancel`) and swap the internals
// for a fetch-to-Vercel-function pattern. Costs ~$0.30/1000 chars on
// ElevenLabs Pro PT-BR voices. Top-of-file comment in voiceInput.ts
// covers a similar swap for the dictation pipeline.

import { showToast } from './toast';

const STORAGE_KEY = 'imersivo:narrator-enabled';

export class Narrator {
  private enabled = false;
  private button: HTMLButtonElement | null = null;
  private voice: SpeechSynthesisVoice | null = null;

  /** Wire the topbar button + restore previously-saved enabled state. */
  install(): void {
    if (!('speechSynthesis' in window)) {
      console.warn('[narrator] speechSynthesis not supported — hiding button');
      const btn = document.getElementById('narrator-button');
      if (btn) btn.style.display = 'none';
      return;
    }
    this.button = document.getElementById('narrator-button') as HTMLButtonElement | null;
    if (!this.button) {
      console.warn('[narrator] #narrator-button missing — skipping wire');
      return;
    }
    this.button.addEventListener('click', () => this.setEnabled(!this.enabled));

    // Restore state from localStorage so the operator's preference survives
    // a refresh.
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === '1') {
        // Don't auto-start narration on the very first room (mobile autoplay
        // rules block speech without a user gesture) — just restore the
        // visual state so the next room change picks up.
        this.enabled = true;
        this.renderButton();
      }
    } catch {
      /* ignore localStorage failures */
    }

    // Voice list loads asynchronously on some browsers.
    this.selectVoice();
    window.speechSynthesis.addEventListener('voiceschanged', () => this.selectVoice());
  }

  /** Toggle on/off. Cancels any in-flight utterance when switching off. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.cancel();
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      /* ignore quota errors */
    }
    this.renderButton();
    if (on) {
      showToast('Tour narrado ativado — descrições em pt-BR ao trocar de sala', { tone: 'info' });
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Speak the given text using the selected pt-BR voice. No-ops when
   * `setEnabled(false)`. Cancels any previous utterance so room-change
   * transitions don't queue up overlapping narration.
   */
  speak(text: string | undefined): void {
    if (!this.enabled || !text) return;
    if (!('speechSynthesis' in window)) return;
    this.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'pt-BR';
    utter.rate = 1.0;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    if (this.voice) utter.voice = this.voice;
    window.speechSynthesis.speak(utter);
  }

  /** Stop the current utterance (and clear the pending queue, if any). */
  cancel(): void {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
  }

  // ─────────────────────────────────────────────────────────────────

  private selectVoice(): void {
    const voices = window.speechSynthesis.getVoices();
    // Prefer pt-BR explicitly, then any pt-*, then default.
    this.voice =
      voices.find((v) => v.lang === 'pt-BR') ||
      voices.find((v) => v.lang.startsWith('pt')) ||
      null;
  }

  private renderButton(): void {
    if (!this.button) return;
    const icon = this.button.querySelector('.icon-button__icon') as HTMLElement | null;
    if (icon) icon.textContent = this.enabled ? '🎙️' : '🔈';
    this.button.setAttribute('aria-pressed', String(this.enabled));
    this.button.classList.toggle('is-active', this.enabled);
    this.button.setAttribute(
      'aria-label',
      this.enabled ? 'Desligar tour narrado' : 'Ligar tour narrado (pt-BR)',
    );
  }
}
